var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { registerComponent } from '../registryCore';
import { bool, num, setParts, setState, syntheticEvent, } from '../helpers';
import { UI, createGroup } from './shared';
/** Tabs */
registerComponent('tabs', function (props, app) {
    var _a;
    var labels = (_a = props.tabs) !== null && _a !== void 0 ? _a : ['Tab 1', 'Tab 2'];
    var activeTab = num(props, 'activeTab', 0);
    var width = num(props, 'width', 300);
    var tabW = width / labels.length;
    var group = createGroup(app, 'tabs', props, { focusable: true, role: 'tablist' });
    var tabH = 36;
    group.add(app.roundedRect({
        width: width,
        height: tabH + 4,
        cornerRadius: UI().radius,
        fill: UI().surface,
        stroke: UI().border,
        strokeWidth: 1,
        listening: false,
    }));
    group.add(app.roundedRect({
        width: tabW - 8,
        height: 2,
        x: activeTab * tabW + 4,
        y: tabH + 1,
        cornerRadius: 1,
        fill: UI().primary,
        listening: false,
    }));
    labels.forEach(function (label, i) {
        var tab = app.group({ x: i * tabW + 4, y: 2, listening: true, focusable: true, metadata: { tabIndex: i } });
        var active = i === activeTab;
        tab.add(app.text({
            text: label,
            fontSize: UI().fontSize,
            fontWeight: active ? '600' : '500',
            fill: active ? UI().primary : UI().textMuted,
            x: (tabW - 8) / 2,
            y: 10,
            textAlign: 'center',
            listening: false,
        }));
        tab.on('click', function () {
            var _a;
            setState(group, { activeTab: i });
            group.emit('change', syntheticEvent('change', group, { value: i, tab: label }));
            (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        });
        group.add(tab);
    });
    setState(group, { tabs: labels, activeTab: activeTab, width: width });
    return group;
});
/** Accordion */
registerComponent('accordion', function (props, app) {
    var _a;
    var sections = (_a = props.sections) !== null && _a !== void 0 ? _a : [
        { title: 'Section 1', content: 'Content 1' },
        { title: 'Section 2', content: 'Content 2' },
    ];
    var expanded = num(props, 'expandedIndex', 0);
    var group = createGroup(app, 'accordion', props, { focusable: true });
    sections.forEach(function (sec, i) {
        var y = i * 44;
        var header = app.group({ x: 0, y: y, listening: true, focusable: true });
        var isOpen = i === expanded;
        header.add(app.roundedRect({
            width: num(props, 'width', 280),
            height: 40,
            cornerRadius: UI().radiusSm,
            fill: isOpen ? UI().primaryMuted : UI().surfaceMuted,
            stroke: UI().border,
            strokeWidth: 1,
            listening: false,
        }), app.text({
            text: (isOpen ? '▼  ' : '▶  ') + sec.title,
            fontSize: UI().fontSize,
            fontWeight: '600',
            fill: UI().text,
            x: 14,
            y: 11,
            listening: false,
        }));
        header.on('click', function () {
            var _a;
            setState(group, { expandedIndex: i });
            group.emit('change', syntheticEvent('change', group, { value: i, section: sec.title }));
            (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        });
        group.add(header);
        if (i === expanded) {
            group.add(app.text({
                text: sec.content,
                x: 14,
                y: y + 46,
                fontSize: UI().fontSize,
                fill: UI().textSecondary,
                listening: false,
            }));
        }
    });
    setState(group, { sections: sections, expandedIndex: expanded });
    return group;
});
/** Toolbar */
registerComponent('toolbar', function (props, app) {
    var _a, _b, _c;
    var rawItems = (_b = (_a = props.items) !== null && _a !== void 0 ? _a : props.buttons) !== null && _b !== void 0 ? _b : ['New', 'Open', 'Save'];
    var icons = (_c = props.icons) !== null && _c !== void 0 ? _c : [];
    var group = createGroup(app, 'toolbar', props, { focusable: true, role: 'toolbar' });
    var x = 0;
    var iconIdx = 0;
    rawItems.forEach(function (item) {
        if (item === '|' || item === null) {
            group.add(app.rect({
                x: x + 2,
                y: 6,
                width: 1,
                height: 20,
                fill: UI().border,
                listening: false,
            }));
            x += 8;
            return;
        }
        var icon = icons[iconIdx] ? "".concat(icons[iconIdx], " ") : '';
        iconIdx += 1;
        var label = item;
        var btnW = Math.max((icon + label).length * 8 + 24, 68);
        var btn = createGroup(app, 'button', { label: label, width: btnW, height: 32, variant: 'ghost' }, { x: x, y: 0, focusable: true, role: 'button' });
        btn.add(app.roundedRect({
            width: btnW,
            height: 32,
            cornerRadius: UI().radiusSm,
            fill: UI().surface,
            stroke: UI().border,
            strokeWidth: 1,
            shadow: UI().shadowSm,
            listening: false,
        }), app.text({
            text: icon + label,
            fontSize: UI().fontSizeSm,
            fontWeight: '600',
            fill: UI().textSecondary,
            x: btnW / 2,
            y: 8,
            textAlign: 'center',
        }));
        btn.on('click', function () {
            group.emit('select', syntheticEvent('select', group, { item: label }));
        });
        group.add(btn);
        x += btnW + 4;
    });
    setState(group, { buttons: rawItems.filter(function (i) { return i && i !== '|'; }), items: rawItems, icons: icons, width: props.width });
    return group;
});
/** Status Bar */
registerComponent('statusBar', function (props, app) {
    var _a;
    var segments = (_a = props.segments) !== null && _a !== void 0 ? _a : ['Ready', 'Line 1', 'UTF-8'];
    var width = num(props, 'width', 400);
    var height = 28;
    var primaryIndex = num(props, 'primaryIndex', 0);
    var mono = bool(props, 'mono', false);
    var group = createGroup(app, 'statusBar', props, { role: 'status' });
    var bg = app.rect({
        width: width,
        height: height,
        fill: UI().surfaceInset,
        stroke: UI().border,
        strokeWidth: 1,
        listening: false,
    });
    group.add(bg);
    var segW = width / segments.length;
    var primarySeg;
    var texts = [];
    segments.forEach(function (seg, i) {
        if (i === primaryIndex) {
            primarySeg = app.rect({
                x: i * segW,
                y: 0,
                width: segW,
                height: height,
                fill: UI().primaryMuted,
                listening: false,
            });
            group.add(primarySeg);
        }
        var textNode = app.text({
            text: seg,
            x: i * segW + 12,
            y: 6,
            fontSize: mono ? 11 : UI().fontSizeSm,
            fontFamily: mono ? 'monospace' : UI().font,
            fontWeight: i === primaryIndex ? '600' : '500',
            fill: i === primaryIndex ? UI().text : UI().textMuted,
            listening: false,
        });
        group.add(textNode);
        texts.push(textNode);
    });
    setParts(group, __assign(__assign({ bg: bg }, (primarySeg ? { primarySeg: primarySeg } : {})), Object.fromEntries(texts.map(function (t, i) { return ["seg".concat(i), t]; }))));
    setState(group, { segments: segments, width: width, primaryIndex: primaryIndex, mono: mono });
    return group;
});
